/**
 * The 11 analytics variables the Hired Candidates ledger edits.
 *
 * One declaration drives the grid columns, the sub-900px detail panel, the
 * value formatters, and the PATCH payload builder — so a column can never
 * render one way and save another.
 *
 * Mirrors backend ``OUTCOME_FIELD_NAMES`` (app/schemas/candidate_outcome.py),
 * in the same order. The two enum fields and the three booleans match the DB
 * CHECK constraints exactly; anything else would 422.
 *
 * ── Groups ───────────────────────────────────────────────────────────────────
 * "Submitted to client" / "Placed" are staffing-agency concepts. The first five
 * variables are therefore the AGENCY group and the last six the EMPLOYMENT
 * group. In-house tenants get Employment by default and a de-emphasised Agency
 * toggle — de-emphasised, never deleted: an in-house team may still track a
 * client-side hand-off, and hiding data someone already recorded is worse than
 * showing a group they ignore.
 */

/** Backend column name for one of the 11 variables. */
export type LedgerFieldKey =
  | "submitted_to_client_at"
  | "client_callback_at"
  | "offer_extended_at"
  | "placed_at"
  | "placement_failed_reason"
  | "hired_at"
  | "start_date"
  | "still_employed_at_90d"
  | "still_employed_at_180d"
  | "performance_rating_at_180d"
  | "would_rehire";

type LedgerGroup = "agency" | "employment";

/**
 * How the cell edits and how its value round-trips.
 *   datetime — date input; sent as an ISO instant at UTC midnight (column is timestamptz)
 *   date     — date input; sent as a bare YYYY-MM-DD (column is DATE)
 *   bool     — tri-state select (Yes / No / unset)
 *   enum     — select over a fixed option list
 */
type LedgerFieldKind = "datetime" | "date" | "bool" | "enum";

interface LedgerFieldOption {
  value: string;
  label: string;
}

export interface LedgerField {
  key: LedgerFieldKey;
  /** Column header — short enough for a grid header. */
  label: string;
  /** Longer form for the detail panel + the header tooltip. */
  help: string;
  group: LedgerGroup;
  kind: LedgerFieldKind;
  /** Grid column width. */
  width: string;
  /**
   * Backend sort key when this column is sortable. Only the date variables are
   * — the enums and booleans have no meaningful server-side ordering, and a
   * header that looks sortable but silently no-ops is worse than a plain one.
   */
  sortKey?: string;
  options?: readonly LedgerFieldOption[];
}

const PLACEMENT_FAILED_REASONS: readonly LedgerFieldOption[] = [
  { value: "withdrew", label: "Withdrew" },
  { value: "rejected_post_offer", label: "Rejected post-offer" },
  { value: "replaced_within_guarantee", label: "Replaced in guarantee" },
  { value: "other", label: "Other" },
];

const PERFORMANCE_RATINGS: readonly LedgerFieldOption[] = [
  { value: "high", label: "High performer" },
  { value: "meets", label: "Meets the bar" },
  { value: "below", label: "Below the bar" },
  { value: "dnf", label: "Did not finish" },
];

export const LEDGER_FIELDS: readonly LedgerField[] = [
  {
    key: "submitted_to_client_at",
    label: "Submitted",
    help: "Date the candidate's profile went to the client.",
    group: "agency",
    kind: "datetime",
    width: "132px",
    sortKey: "submitted_to_client_at",
  },
  {
    key: "client_callback_at",
    label: "Client callback",
    help: "Date the client came back on this candidate.",
    group: "agency",
    kind: "datetime",
    width: "138px",
    sortKey: "client_callback_at",
  },
  {
    key: "offer_extended_at",
    label: "Offer extended",
    help: "Date an offer was put in front of the candidate.",
    group: "agency",
    kind: "datetime",
    width: "138px",
    sortKey: "offer_extended_at",
  },
  {
    key: "placed_at",
    label: "Placed",
    help: "Date the placement was confirmed with the client.",
    group: "agency",
    kind: "datetime",
    width: "126px",
    sortKey: "placed_at",
  },
  {
    key: "placement_failed_reason",
    label: "If it fell through",
    help: "Why the placement did not hold. Leave blank if it held.",
    group: "agency",
    kind: "enum",
    width: "184px",
    options: PLACEMENT_FAILED_REASONS,
  },
  {
    key: "hired_at",
    label: "Hired",
    help: "Date the hire was agreed.",
    group: "employment",
    kind: "datetime",
    width: "126px",
    sortKey: "hired_at",
  },
  {
    key: "start_date",
    label: "Start date",
    help: "First day on the job.",
    group: "employment",
    kind: "date",
    width: "126px",
    sortKey: "start_date",
  },
  {
    key: "still_employed_at_90d",
    label: "At 90 days",
    help: "Still employed 90 days after starting?",
    group: "employment",
    kind: "bool",
    width: "124px",
  },
  {
    key: "still_employed_at_180d",
    label: "At 180 days",
    help: "Still employed 180 days after starting?",
    group: "employment",
    kind: "bool",
    width: "128px",
  },
  {
    key: "performance_rating_at_180d",
    label: "180-day rating",
    help: "How the hiring manager rated them at the 180-day mark.",
    group: "employment",
    kind: "enum",
    width: "168px",
    options: PERFORMANCE_RATINGS,
  },
  {
    key: "would_rehire",
    label: "Would rehire",
    help: "Would the hiring manager take them again?",
    group: "employment",
    kind: "bool",
    width: "132px",
  },
];

export const LEDGER_FIELD_COUNT = LEDGER_FIELDS.length; // 11

const AGENCY_FIELDS = LEDGER_FIELDS.filter((f) => f.group === "agency");
const EMPLOYMENT_FIELDS = LEDGER_FIELDS.filter((f) => f.group === "employment");

/** Column-group toggle. "all" shows both groups. */
export type LedgerColumnGroup = "all" | "agency" | "employment";

export function fieldsForGroup(group: LedgerColumnGroup): readonly LedgerField[] {
  if (group === "agency") return AGENCY_FIELDS;
  if (group === "employment") return EMPLOYMENT_FIELDS;
  return LEDGER_FIELDS;
}

// ── Value <-> control marshalling ─────────────────────────────────────────────

/** ISO instant or date → "YYYY-MM-DD" for a native date input ("" when unset). */
function toDateInputValue(v: string | null | undefined): string {
  if (!v) return "";
  return v.slice(0, 10);
}

/** Boolean → tri-state select value. */
function toBoolSelectValue(v: boolean | null | undefined): string {
  if (v === true) return "true";
  if (v === false) return "false";
  return "";
}

/**
 * Turn a control's string value into the JSON value the PATCH body carries.
 *
 * An empty control ALWAYS yields `null`, never `undefined` — that is the whole
 * clearing contract. The backend keys on key presence, so a `null` here blanks
 * the cell while omitting the key would leave it untouched.
 */
export function toPatchValue(
  field: LedgerField,
  raw: string,
): string | boolean | null {
  if (raw === "") return null;
  switch (field.kind) {
    case "datetime":
      // Column is timestamptz; the recruiter is picking a day, so pin it to
      // UTC midnight rather than inventing a local time we never asked for.
      return `${raw}T00:00:00Z`;
    case "date":
      return raw;
    case "bool":
      return raw === "true";
    case "enum":
      return raw;
  }
}

/** The control value for a field, given a ledger row. */
export function toControlValue(
  field: LedgerField,
  value: string | boolean | null | undefined,
): string {
  if (value === null || value === undefined) return "";
  if (field.kind === "bool") return toBoolSelectValue(value as boolean);
  if (field.kind === "datetime" || field.kind === "date") {
    return toDateInputValue(value as string);
  }
  return String(value);
}

/** Human-readable cell text. Returns null when the variable is unset. */
export function formatLedgerValue(
  field: LedgerField,
  value: string | boolean | null | undefined,
): string | null {
  if (value === null || value === undefined || value === "") return null;
  switch (field.kind) {
    case "datetime":
    case "date": {
      const iso = String(value).slice(0, 10);
      const [y, m, d] = iso.split("-").map(Number);
      if (!y || !m || !d) return iso;
      // Construct in UTC so a date never drifts a day for western timezones.
      return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
    }
    case "bool":
      return value === true ? "Yes" : "No";
    case "enum":
      return field.options?.find((o) => o.value === value)?.label ?? String(value);
  }
}
